import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Badge, Btn, type BadgeKind } from "../../ui";
import {
  getNecrologioMatchHistory,
  type NecrologioMatchHistoryItem,
} from "../../api";

export function MatchHistoryPanel({
  necrologioId,
  onEnqueue,
  enqueueBusy,
}: {
  necrologioId: number | string;
  onEnqueue: () => void;
  enqueueBusy: boolean;
}) {
  const [history, setHistory] = useState<NecrologioMatchHistoryItem[] | null>(null);

  useEffect(() => {
    setHistory(null);
    getNecrologioMatchHistory(necrologioId)
      .then((r) => setHistory(r.items))
      .catch(() => setHistory([]));
  }, [necrologioId]);

  if (history === null) {
    return (
      <div className="col gap-8" style={{ borderTop: "1px solid var(--line)", paddingTop: 12, fontSize: 12 }}>
        <div className="muted">Caricamento storico…</div>
      </div>
    );
  }

  if (history.length === 0) {
    const outcome = sisterHistoryOutcome(null);
    return (
      <div className="col gap-8" style={{ borderTop: "1px solid var(--line)", paddingTop: 12, fontSize: 12 }}>
        <OutcomeBox outcome={outcome}>
          <Btn size="sm" variant="primary" disabled={enqueueBusy} onClick={onEnqueue}>
            {enqueueBusy ? "Invio…" : "Cerca su SISTER…"}
          </Btn>
        </OutcomeBox>
      </div>
    );
  }

  const lastRun = history[0];
  const outcome = sisterHistoryOutcome(lastRun);
  return (
    <div className="col gap-8" style={{ borderTop: "1px solid var(--line)", paddingTop: 12 }}>
      <div className="row gap-8" style={{ alignItems: "baseline" }}>
        <div className="k" style={{ fontWeight: 600 }}>Storico ricerche</div>
        <Badge kind={outcome.kind}>{outcome.label}</Badge>
        {lastRun.numImmobili != null && lastRun.numImmobili > 0 && (
          <Badge kind="accent">{lastRun.numImmobili} immobili</Badge>
        )}
        {lastRun.matchedCf && <Badge kind="info">CF {lastRun.matchedCf}</Badge>}
      </div>

      <OutcomeBox outcome={outcome}>
        {outcome.kind === "danger" && lastRun.error && (
          <div className="mono" style={{ fontSize: 11, color: "var(--danger)" }}>{lastRun.error}</div>
        )}
      </OutcomeBox>

      {lastRun.omonimi?.length ? (
        <div className="col gap-4" style={{ fontSize: 12 }}>
          <div className="k">Omonimi da disambiguare</div>
          {lastRun.omonimi.map((o, i) => (
            <div key={i} className="mono">
              {o.cognome} {o.nome} · {o.data} · {o.luogo} ({o.prov}) · {o.cf}
            </div>
          ))}
        </div>
      ) : null}

      {history.length > 1 && (
        <details style={{ fontSize: 11 }}>
          <summary className="muted">Cronologia ({history.length} ricerche)</summary>
          <div className="col gap-4" style={{ marginTop: 4 }}>
            {history.map((h) => (
              <div key={h.id} className="mono">
                {new Date(h.searchedAt).toLocaleString("it-IT")} · {sisterStatusLabel(h.status)}
                {h.numImmobili ? ` · ${h.numImmobili} immobili` : ""}
                {h.error ? ` · errore: ${h.error}` : ""}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

interface SisterOutcome {
  kind: BadgeKind;
  label: string;
  title: string;
  detail: string;
  next: string;
}

function sisterHistoryOutcome(lastRun: NecrologioMatchHistoryItem | null): SisterOutcome {
  if (!lastRun) {
    return {
      kind: "warn",
      label: "mai cercato",
      title: "Mai cercato su SISTER",
      detail: "Non esiste ancora uno storico per questo necrologio.",
      next: "Accoda la ricerca nel browser SISTER.",
    };
  }

  switch (lastRun.status) {
    case "visura_ok":
      if ((lastRun.numImmobili ?? 0) > 0) {
        return {
          kind: "info",
          label: "immobili trovati",
          title: "Visura SISTER con immobili",
          detail: `${lastRun.numImmobili ?? 0} immobili collegati al codice fiscale trovato.`,
          next: "Apri Prospects per valutare immobili e province.",
        };
      }
      return {
        kind: "warn",
        label: "visura senza immobili",
        title: "Visura scaricata, immobili non emersi",
        detail: "La ricerca SISTER è conclusa ma non risultano immobili parsati.",
        next: "Controlla «Recupera dati visura» o archivia come senza immobili.",
      };
    case "omonimi_found":
      return {
        kind: "accent",
        label: "omonimi",
        title: "Omonimi trovati",
        detail: `${lastRun.omonimi?.length ?? 0} persone compatibili trovate su SISTER.`,
        next: "Scegli il codice fiscale corretto prima della visura.",
      };
    case "no_match":
      return {
        kind: "muted",
        label: "nessuna corrispondenza",
        title: "Nessuna corrispondenza SISTER",
        detail: "Nome, anno e provincia non hanno prodotto un soggetto univoco.",
        next: "Verifica i dati del necrologio o archivia.",
      };
    case "error":
      return {
        kind: "danger",
        label: "errore",
        title: "Ricerca non completata",
        detail: "Il worker SISTER ha registrato un errore.",
        next: "Rilancia la ricerca dopo aver controllato il browser/plugin.",
      };
    case "pending":
      return {
        kind: "warn",
        label: "in coda",
        title: "Ricerca in coda",
        detail: "La richiesta esiste ma il browser non ha ancora concluso.",
        next: "Attendi il worker SISTER.",
      };
    default:
      return {
        kind: "muted",
        label: sisterStatusLabel(lastRun.status),
        title: "Stato SISTER registrato",
        detail: `Ultimo stato: ${lastRun.status || "sconosciuto"}.`,
        next: "Apri Prospects se la ricerca ha generato un risultato.",
      };
  }
}

function sisterStatusLabel(status: string): string {
  switch (status) {
    case "visura_ok": return "visura OK";
    case "omonimi_found": return "omonimi";
    case "no_match": return "nessuna corrispondenza";
    case "error": return "errore";
    case "pending": return "in coda";
    default: return status || "sconosciuto";
  }
}

function OutcomeBox({ outcome, children }: { outcome: SisterOutcome; children?: ReactNode }) {
  return (
    <div className="preview-box" style={outcomeBoxStyle(outcome.kind)}>
      <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
        <Badge kind={outcome.kind}>{outcome.label}</Badge>
        <strong>{outcome.title}</strong>
      </div>
      <div style={{ marginTop: 8 }}>{outcome.detail}</div>
      <div className="muted" style={{ marginTop: 4 }}>Prossimo passo: {outcome.next}</div>
      {children && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  );
}

function outcomeBoxStyle(kind: BadgeKind): CSSProperties {
  if (kind === "muted") {
    return { padding: "12px 14px", lineHeight: 1.5 };
  }
  const borderColor = kind === "accent" ? "var(--accent-line)" : `var(--${kind})`;
  return {
    padding: "12px 14px",
    lineHeight: 1.5,
    background: `var(--${kind}-dim)`,
    borderColor,
  };
}
